import React, { useMemo } from 'react';
import SubsurfaceViewer3D from './SubsurfaceViewer3D';
import TerrainMesh from './terrain/TerrainMesh';
import DatasetPointClouds from './DatasetPointClouds';
import Boreholes3D from './boreholes/Boreholes3D';
import useTerrainSettings from '../hooks/useTerrainSettings';
import { BASEMAP_TILE_SOURCES } from '../lib/map-styles';
import { useDemoMode } from '../hooks/useDemoMode';
import { hasXyz } from '../lib/dataset-points3d';

/**
 * The 3D scene shared by the /3d page and the docked map panel: SubsurfaceViewer3D with the DEM
 * terrain, boreholes and dataset point clouds as scene extras. `view` is the state object of
 * hooks/useViewer3dView; models come from hooks/useViewer3dModels. The caller owns the layout.
 */
const Viewer3DScene = React.forwardRef(function Viewer3DScene({
  view, datasets = [], drawnIds = [], points = null, focusLine = null, fenceModels, fitModelIds = null, showPointCloud = true, style,
}, ref) {
  const terrain = useTerrainSettings();
  const [demoMode] = useDemoMode();
  const drawnDatasets = useMemo(() => datasets.filter((d) => drawnIds.includes(d.id) && hasXyz(d)), [datasets, drawnIds]);
  const clouds = points?.clouds;
  const { terrainReady, setTerrainReady, showFences, fenceOpacity } = view;

  return (
    <SubsurfaceViewer3D
      ref={ref}
      datasets={datasets}
      focusLine={focusLine}
      fenceModels={fenceModels}
      sceneExtras={({ origin }) => (
        <>
          <TerrainMesh origin={origin} models={fenceModels} opacity={fenceOpacity} showModels={showFences} onReady={setTerrainReady}
            fallback={(
              <>
                <DatasetPointClouds datasets={drawnDatasets} clouds={clouds} origin={origin} />
                <Boreholes3D origin={origin} />
              </>
            )}
            renderDraped={({ groundY, refElev }) => (
              <>
                <DatasetPointClouds datasets={drawnDatasets} clouds={clouds} origin={origin} refElev={refElev} groundY={groundY} />
                <Boreholes3D origin={origin} groundY={groundY} />
              </>
            )} />
        </>
      )}
      terrainDraped={terrainReady && showFences}
      textureAttribution={terrain.drape3d ? BASEMAP_TILE_SOURCES[terrain.drapeStyle]?.attribution : null}
      center={{ lat: 37.7749, lng: -122.4194 }}
      style={style || { width: '100%', height: '100%' }}
      showPointCloud={showPointCloud}
      demoMode={demoMode}
      colorMode={view.colorMode}
      cameraPresetRequest={view.cameraPresetRequest}
      fitModelIds={fitModelIds}
      threshold={view.threshold} setThreshold={view.setThreshold}
      sliceAxis={view.sliceAxis} setSliceAxis={view.setSliceAxis}
      slicePos={view.slicePos} setSlicePos={view.setSlicePos}
      sliceThickness={view.sliceThickness} setSliceThickness={view.setSliceThickness}
      cropX={view.cropX} setCropX={view.setCropX}
      cropZ={view.cropZ} setCropZ={view.setCropZ}
      showVolumePanel={view.showVolumePanel} setShowVolumePanel={view.setShowVolumePanel}
      showFences={view.showFences} setShowFences={view.setShowFences}
      fenceOpacity={view.fenceOpacity} setFenceOpacity={view.setFenceOpacity}
      onResetVolumeFilters={view.resetVolumeFilters}
    />
  );
});

export default Viewer3DScene;
